/**
 * ProjectEnvironmentCard - one shared environment of a project, as a card.
 *
 * Not a table row, unlike the personal environments beside it: a project
 * environment is a *place* people work in and ask questions about — how fresh
 * is it, what is missing, where is its preview, what data does it hold — and
 * five facts of five shapes do not fit in columns.
 *
 * Freshness is fetched per card rather than taken from the list: measuring it
 * runs a `git fetch`, so only an open card pays for it, and the number a person
 * is looking at is the one measured while they looked.
 *
 * Every button carries its own permission. The tab this lives in is governed by
 * `p.env.use` — enough to look, to list the copies and to open a thread — and
 * nothing inside inherits that: destroying is `p.env.manage`, and changing what
 * is inside the environment (code or data) is `p.env.sync`.
 *
 * None of the operation buttons operate anything. They open a thread with an
 * agent and the message already written, and a person presses Send. That
 * indirection is the feature: a sync needs judgement between the preflight and
 * the merge, and the thread is where that judgement ends up written down.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import type { EnvironmentId, ProjectId, QaOperationKind, ThreadId } from "@taskin-code/contracts";

import {
  type AdminEnvironmentRow,
  type AdminEnvironmentSnapshotRow,
  EnvironmentsClient,
} from "../../auth/EnvironmentsClient";
import { environmentQueryKeys } from "../../lib/environmentQueries";
import { formatRelativeTime } from "../../lib/relativeTime";
import { useUserDirectoryMap } from "../../lib/userDirectoryReactQuery";
import { Button } from "../ui/button";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";
import { environmentStatusLabel, formatBytes, previewUrl } from "./EnvironmentsSection.logic";
import {
  baselineSnapshotOf,
  describeActiveOperation,
  describeActiveTrial,
  describeClonedFrom,
  describeLastModification,
  environmentPill,
} from "./projectEnvironments.logic";
import { PrPreviewList } from "./PrPreviewList";
import { SnapshotList } from "./SnapshotList";

/** Why the operation buttons are greyed out for somebody who may only look. */
const SYNC_DISABLED_REASON = "Necesitas p.env.sync en este proyecto";

/** Why «Actualizar» is inert while somebody is trying a pull request here. */
const TRIAL_BLOCKS_SYNC_REASON = "Hay un PR en prueba";

/** Why «Volver a la base» is greyed out for somebody who may only look. */
const PREVIEW_DISABLED_REASON = "Necesitas p.pr.preview en este proyecto";

export function ProjectEnvironmentCard(props: {
  readonly row: AdminEnvironmentRow;
  readonly projectId: ProjectId;
  readonly known: ReadonlyArray<AdminEnvironmentRow>;
  /** `p.env.manage`: creating and destroying the environments of a project. */
  readonly canManage: boolean;
  /** `p.env.sync`: changing the code or the data inside the environment. */
  readonly canSync: boolean;
  /** `p.pr.preview`: putting a pull request on the environment, and taking it off. */
  readonly canPreview: boolean;
  readonly busy: boolean;
  readonly onOpenThread: (environmentId: EnvironmentId) => void;
  /**
   * Open a thread for an operation on this environment. The prompt is fetched
   * here — it depends on which snapshot the person picked — and handed over
   * whole, so the parent stays the single place that opens the dialog.
   */
  readonly onOpenOperation: (input: {
    readonly environmentId: EnvironmentId;
    readonly operation: QaOperationKind;
    readonly prompt: string;
  }) => void;
  /**
   * Go to a thread this environment points at — the one holding it right now,
   * or the one it was last changed from. Handed down rather than navigated
   * here so the card keeps no router dependency: it is rendered inside the
   * settings screen, and every other thing it does to the outside world is
   * already a callback.
   */
  readonly onOpenThreadPage: (threadId: ThreadId) => void;
  readonly onDestroy: () => void;
}) {
  const { row } = props;
  const [showSnapshots, setShowSnapshots] = useState(false);
  const [operationError, setOperationError] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  // Only to put a name on `activeOperation.userId` and `lastModifiedBy`. Every
  // authenticated account may read the directory, which is what makes this
  // usable by a PM who is not an administrator.
  const directory = useUserDirectoryMap();

  // Measured through React Query, keyed under `environmentQueryKeys`: the same
  // key `__root` invalidates when an `environment.*` domain event arrives, so
  // the 10-minute sweep's result reaches this card on its own.
  const syncQuery = useQuery({
    queryKey: environmentQueryKeys.syncState(row.id),
    queryFn: () => EnvironmentsClient.syncState(row.id),
    // Measuring runs a `git fetch`; only an open card pays for it, and once.
    staleTime: 60_000,
    retry: false,
  });

  // Through the user-facing route, not the admin one: the person who resets an
  // environment's data has `p.env.sync` and, usually, no administrator role —
  // and a «Resetear datos» menu that could only offer the baseline would hide
  // from them exactly the copies they made to reset back to.
  const snapshotsQuery = useQuery({
    queryKey: environmentQueryKeys.snapshots(row.id),
    queryFn: () => EnvironmentsClient.usableSnapshots(row.id),
    // Listing them stats every dump file, so only an open card pays for it.
    staleTime: 60_000,
    retry: false,
  });

  const sync = syncQuery.data ?? null;
  const syncError =
    syncQuery.error instanceof Error
      ? syncQuery.error.message
      : syncQuery.error !== null
        ? "No se pudo medir la frescura"
        : null;
  // Silent on failure: the data line is a detail of the card, and losing the
  // whole card because a dump file could not be stat'd helps nobody.
  const snapshots = snapshotsQuery.data ?? null;

  // The stored state until the measurement lands, so the card never opens blank.
  // The trial wins over freshness when there is one: an environment wearing a
  // pull request really is diverged from its base branch, and saying so instead
  // would answer a question nobody asked.
  const trial = row.activeTrial;
  const pill = environmentPill({
    activeTrial: trial,
    syncState: sync?.syncState ?? row.syncState,
    behindBy: sync?.behindBy ?? row.behindBy,
  });
  const checkedAt = sync?.checkedAt ?? row.syncCheckedAt;
  const trackedBranch = sync?.trackedBranch ?? row.trackedBranch;
  const url = previewUrl(row);
  const clonedFrom = describeClonedFrom(row, props.known);
  const pendingCommits = sync?.pendingCommits ?? [];
  const baseline = baselineSnapshotOf(snapshots?.snapshots ?? []);

  // Who is holding the environment right now, and who last wrote to it. Both
  // come from the list request, so they are as fresh as the last refetch —
  // which is what `environmentQueryKeys.all` invalidating on every
  // `environment.*` domain event is for.
  const activeOperation = row.activeOperation;
  const activeLine =
    activeOperation === null
      ? null
      : describeActiveOperation({
          operation: activeOperation.operation,
          actorName:
            activeOperation.userId === null
              ? null
              : (directory.get(activeOperation.userId)?.displayName ?? null),
        });
  const trialLine =
    trial === null
      ? null
      : describeActiveTrial({
          prNumber: trial.prNumber,
          actorName:
            trial.startedBy === null ? null : (directory.get(trial.startedBy)?.displayName ?? null),
          when: `hace ${formatRelativeTime(trial.startedAt)}`,
        });
  const lastModifiedLine = describeLastModification({
    at: row.lastModifiedAt,
    kind: row.lastModifiedKind,
    authorName:
      row.lastModifiedBy === null ? null : (directory.get(row.lastModifiedBy)?.displayName ?? null),
    when: row.lastModifiedAt === null ? null : `hace ${formatRelativeTime(row.lastModifiedAt)}`,
  });

  // An environment takes one operation at a time (the server holds a mutex per
  // environment), so offering a second one would only produce a refusal.
  const operationsBlocked = row.status !== "ready" || activeOperation !== null || preparing;

  /**
   * Fetch the message and hand the operation up.
   *
   * The prompt is rendered by the server — it mixes the row, the project's
   * title and the project's own overrides in `.taskin/config.json` — so the
   * button waits for it rather than opening a dialog it would have to fill in
   * afterwards.
   */
  const openOperation = async (
    operation: QaOperationKind,
    options?: { readonly snapshotLabel?: string; readonly sourceName?: string },
  ) => {
    setOperationError(null);
    setPreparing(true);
    try {
      const prompts = await EnvironmentsClient.prompts(row.id, options ?? {});
      const prompt =
        operation === "sync"
          ? prompts.sync
          : operation === "reset-data"
            ? prompts.resetData
            : operation === "back-to-base"
              ? prompts.backToBase
              : operation === "try-pr"
                ? prompts.tryPr
                : prompts.clone;
      props.onOpenOperation({ environmentId: row.id, operation, prompt });
    } catch (cause) {
      setOperationError(
        cause instanceof Error ? cause.message : "No se pudo preparar el mensaje del hilo.",
      );
    } finally {
      setPreparing(false);
    }
  };

  /** The copies «Resetear datos» offers, newest first, baseline aside. */
  const resettableSnapshots: ReadonlyArray<AdminEnvironmentSnapshotRow> = (
    snapshots?.snapshots ?? []
  )
    .filter((snapshot) => snapshot.snapshotId !== baseline?.snapshotId)
    .toSorted((left, right) => right.createdAt.localeCompare(left.createdAt))
    .slice(0, 8);

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card/40 p-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <h4 className="truncate text-[15px] font-semibold tracking-tight">
            {row.name ?? row.branch}
          </h4>
          <span className="truncate text-xs text-muted-foreground">
            {trackedBranch === null ? (
              "sin rama base"
            ) : (
              <>
                sigue <span className="font-mono">{trackedBranch}</span>
              </>
            )}
          </span>
        </div>
        {row.status === "ready" ? (
          <span
            title={pill.hint}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${pill.className}`}
          >
            <span className={`size-1.5 rounded-full ${pill.dotClassName}`} aria-hidden="true" />
            {pill.label}
          </span>
        ) : (
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-muted/50 px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground">
            {environmentStatusLabel(row.status)}
          </span>
        )}
      </header>

      {row.failureReason !== null ? (
        <p className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          {row.failureReason}
        </p>
      ) : null}

      <dl className="flex flex-col gap-1.5 text-xs">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <dt className="text-muted-foreground">Estado:</dt>
          <dd className="min-w-0">
            {checkedAt === null ? (
              <span className="text-muted-foreground">sin comprobar todavía</span>
            ) : (
              <span className="text-muted-foreground">
                comprobado hace {formatRelativeTime(checkedAt)}
              </span>
            )}
            {syncError !== null ? <span className="ml-2 text-amber-400">{syncError}</span> : null}
          </dd>
        </div>

        {pendingCommits.length > 0 ? (
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
            <dt className="text-muted-foreground">Pendientes:</dt>
            <dd className="min-w-0 truncate">
              {pendingCommits.slice(0, 3).map((commit, index) => (
                <span key={commit.sha}>
                  {index > 0 ? " · " : ""}
                  <span className="font-mono">{commit.sha.slice(0, 7)}</span> {commit.title}
                </span>
              ))}
              {pendingCommits.length > 3 ? ` · +${pendingCommits.length - 3} más` : ""}
            </dd>
          </div>
        ) : null}

        <div className="flex flex-wrap items-baseline gap-x-2">
          <dt className="text-muted-foreground">Preview:</dt>
          <dd className="min-w-0">
            {url === null ? (
              <span className="text-muted-foreground">sin arrancar</span>
            ) : (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="text-primary underline underline-offset-2"
              >
                {row.devHostname}
              </a>
            )}
          </dd>
        </div>

        <div className="flex flex-wrap items-baseline gap-x-2">
          <dt className="text-muted-foreground">Datos:</dt>
          <dd className="min-w-0 text-muted-foreground">
            {snapshots === null ? (
              "leyendo copias…"
            ) : snapshots.error !== null ? (
              <span className="text-amber-400">no se pudo leer el almacén: {snapshots.error}</span>
            ) : (
              <>
                {snapshots.snapshots.length} {snapshots.snapshots.length === 1 ? "copia" : "copias"}{" "}
                · {formatBytes(snapshots.totalBytes)}
                {baseline === null
                  ? " · sin baseline"
                  : ` · baseline ${new Date(baseline.createdAt).toLocaleDateString()}`}
              </>
            )}
          </dd>
        </div>

        <div className="flex flex-wrap items-baseline gap-x-2">
          <dt className="text-muted-foreground">Rama:</dt>
          <dd className="min-w-0 truncate font-mono">{row.branch}</dd>
        </div>

        {clonedFrom !== null ? <div className="text-muted-foreground/80">{clonedFrom}</div> : null}
      </dl>

      {trialLine !== null ? (
        <p className="flex flex-wrap items-center gap-1.5 rounded border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs text-sky-700 dark:text-sky-200">
          <span>{trialLine}</span>
          {trial?.threadId != null ? (
            <button
              type="button"
              className="underline underline-offset-2"
              onClick={() => props.onOpenThreadPage(trial.threadId!)}
            >
              Ver el hilo
            </button>
          ) : null}
        </p>
      ) : null}

      {activeLine !== null ? (
        <p className="flex flex-wrap items-center gap-1.5 rounded border border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          <span>{activeLine}</span>
          {activeOperation?.threadId != null ? (
            <button
              type="button"
              className="text-primary underline underline-offset-2"
              onClick={() => props.onOpenThreadPage(activeOperation.threadId!)}
            >
              Ver el hilo
            </button>
          ) : null}
        </p>
      ) : null}

      {/* The pull requests running off this environment right now. Its own
          component: it owns a request and two mutations, and the previews of a
          shared environment are read next to that environment's data, not in a
          list of their own that would have to name it again. */}
      <PrPreviewList
        projectId={props.projectId}
        baseEnvironmentId={row.id}
        canPreview={props.canPreview}
        onOpenThreadPage={props.onOpenThreadPage}
      />

      <div className="flex flex-wrap items-center gap-2 pt-1">
        {/* «Actualizar» does not update anything: it opens a thread with the
            message written, so that every update leaves a record of what was
            decided and why. `p.env.sync` all the same — the thread it opens
            exists to change the environment. */}
        <Button
          size="sm"
          disabled={!props.canSync || operationsBlocked || trial !== null}
          title={
            !props.canSync
              ? SYNC_DISABLED_REASON
              : trial !== null
                ? TRIAL_BLOCKS_SYNC_REASON
                : undefined
          }
          onClick={() => void openOperation("sync")}
        >
          {trackedBranch === null ? "Actualizar" : `Actualizar con ${trackedBranch}`}
        </Button>
        {/* Only while there is something to take off. It opens a thread like
            every other operation button — the agent restores the data as well
            as the code, and the thread is where it says whether both worked. */}
        {trial !== null ? (
          <Button
            size="sm"
            variant="outline"
            disabled={!props.canPreview || operationsBlocked}
            {...(props.canPreview ? {} : { title: PREVIEW_DISABLED_REASON })}
            onClick={() => void openOperation("back-to-base")}
          >
            Volver a la base
          </Button>
        ) : null}
        {props.canSync ? (
          <Menu>
            <MenuTrigger
              render={<Button size="sm" variant="outline" disabled={operationsBlocked} />}
            >
              Resetear datos ▾
            </MenuTrigger>
            <MenuPopup align="start">
              <MenuItem onClick={() => void openOperation("reset-data")}>
                {baseline === null ? "Al estado base (baseline)" : `Al baseline`}
              </MenuItem>
              {resettableSnapshots.map((snapshot) => (
                <MenuItem
                  key={snapshot.snapshotId}
                  onClick={() =>
                    void openOperation("reset-data", { snapshotLabel: snapshot.label })
                  }
                >
                  A «{snapshot.label}»
                </MenuItem>
              ))}
            </MenuPopup>
          </Menu>
        ) : null}
        {props.canSync && props.known.length > 1 ? (
          <Menu>
            <MenuTrigger
              render={<Button size="sm" variant="outline" disabled={operationsBlocked} />}
            >
              Copiar desde…
            </MenuTrigger>
            <MenuPopup align="start">
              {props.known
                .filter(
                  (candidate) =>
                    candidate.id !== row.id &&
                    candidate.deletedAt === null &&
                    candidate.status === "ready",
                )
                .map((candidate) => (
                  <MenuItem
                    key={candidate.id}
                    onClick={() =>
                      void openOperation("clone", {
                        sourceName: candidate.name ?? candidate.branch,
                      })
                    }
                  >
                    {candidate.name ?? candidate.branch}
                  </MenuItem>
                ))}
            </MenuPopup>
          </Menu>
        ) : null}
        <Button
          size="sm"
          variant="outline"
          disabled={row.status !== "ready"}
          onClick={() => props.onOpenThread(row.id)}
        >
          Abrir hilo aquí
        </Button>
        <Button size="sm" variant="outline" onClick={() => setShowSnapshots((open) => !open)}>
          {showSnapshots ? "Ocultar copias" : "Copias"}
        </Button>
        {props.canManage ? (
          <Button
            size="sm"
            variant="outline"
            className="text-red-500 hover:text-red-400"
            disabled={props.busy}
            onClick={props.onDestroy}
          >
            {props.busy ? "Destruyendo…" : "Destruir"}
          </Button>
        ) : null}
      </div>

      {operationError !== null ? (
        <p className="text-[11px] text-red-400">{operationError}</p>
      ) : null}

      {/* Said out loud rather than left as a greyed-out button: without
          `p.env.sync` the operation buttons are inert, and a control with no
          explanation reads as broken. */}
      {!props.canSync ? (
        <p className="text-[11px] text-muted-foreground/80">
          Puedes mirar y abrir hilos aquí. Para actualizar o resetear los datos hace falta{" "}
          <code>p.env.sync</code> en este proyecto.
        </p>
      ) : null}

      {lastModifiedLine !== null ? (
        <p className="text-[11px] text-muted-foreground/80">
          {lastModifiedLine}
          {row.lastModifiedThreadId !== null ? (
            <>
              {" · "}
              <button
                type="button"
                className="text-primary underline underline-offset-2"
                onClick={() => props.onOpenThreadPage(row.lastModifiedThreadId!)}
              >
                en su hilo
              </button>
            </>
          ) : null}
        </p>
      ) : null}

      {showSnapshots && snapshots !== null ? <SnapshotList snapshots={snapshots} /> : null}
    </section>
  );
}
